import { useAnalytics } from '@/contexts/analytics-context/use-analytics';
import { isSameMonth, subMonths, UTCDate } from '@/utils/dates';
import {
  ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  OnChangeFn,
  useReactTable,
} from '@tanstack/react-table';
import { useEffect, useRef, useState } from 'react';
import { createSearchParams, useNavigate } from 'react-router';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../design-system/table/table';
import { CategoryView, VendorView } from '../../../../types/entity-types';
import { SupportedCountryCurrency } from '../../../../types/supported-country-currency';
import { cn } from '../../../../utils/cn';
import { useEntityModalState } from '../../../../utils/use-entity-modal-state';
import { ReportingBalances } from '../../utils/get-reporting-balances';
import { ReportingTotals } from '../../utils/get-reporting-data';
import { VendorDetailsDrawer } from '../vendor-details-drawer/vendor-details-drawer';
import { ReportingBalanceRow } from './reporting-balance-row';
import { reportingTableColumnsDefinition } from './reporting-table-columns-definition';

const MONTH_AMOUNT_COLUMN_IDS = [
  'three_months_ago_amount',
  'two_months_ago_amount',
  'previous_month_amount',
  'current_month_amount',
];

type VendorModalView = 'vendor' | 'category';

type ReportingDataTableProps = {
  data: ReportingTotals;
  onMoveVendor: ({ vendorId, parentRowId }: { vendorId: string; parentRowId: string }) => void;
  onMoveCategory: ({ id, parentRowId, level1 }: { id: string; parentRowId: string; level1?: string }) => void;
  expandedState: ExpandedState;
  baseCurrency: SupportedCountryCurrency;
  setExpandedState: OnChangeFn<ExpandedState>;
  initialScrollToRowId: string | undefined;
  categories: CategoryView[];
  referenceDate?: Date;
  highlightedMonth?: Date;
  reportingBalances?: ReportingBalances;
};

export const ReportingDataTable = ({
  data,
  onMoveVendor,
  onMoveCategory,
  expandedState,
  baseCurrency,
  setExpandedState,
  initialScrollToRowId,
  categories,
  referenceDate,
  highlightedMonth,
  reportingBalances,
}: ReportingDataTableProps) => {
  const navigate = useNavigate();
  const analytics = useAnalytics();
  const {
    selectedId: selectedVendorId,
    mutations: entityModalMutations,
    modalView,
  } = useEntityModalState<VendorModalView>({
    defaultView: 'vendor',
  });
  const parentRowRef = useRef<HTMLTableRowElement>(null);
  const hasBeenMountedRef = useRef(false);
  const [tableWidth, setTableWidth] = useState(0);

  const tableRef = useRef<HTMLTableElement>(null);

  // Compute months and highlighted states for meta
  const effectiveReferenceDate = referenceDate ?? new UTCDate();
  const month3 = subMonths(effectiveReferenceDate, 3);
  const month2 = subMonths(effectiveReferenceDate, 2);
  const month1 = subMonths(effectiveReferenceDate, 1);
  const month0 = effectiveReferenceDate;

  const isMonth3Highlighted = highlightedMonth ? isSameMonth(highlightedMonth, month3) : false;
  const isMonth2Highlighted = highlightedMonth ? isSameMonth(highlightedMonth, month2) : false;
  const isMonth1Highlighted = highlightedMonth ? isSameMonth(highlightedMonth, month1) : false;
  const isMonth0Highlighted = highlightedMonth ? isSameMonth(highlightedMonth, month0) : false;
  const isCurrentMonth = isSameMonth(effectiveReferenceDate, new UTCDate());

  // Helper function to check if a month column is highlighted
  const isColumnHighlighted = (columnId: string) => {
    return (
      (columnId === 'three_months_ago_amount' && isMonth3Highlighted) ||
      (columnId === 'two_months_ago_amount' && isMonth2Highlighted) ||
      (columnId === 'previous_month_amount' && isMonth1Highlighted) ||
      (columnId === 'current_month_amount' && isMonth0Highlighted)
    );
  };

  // Helper function to get month column classes (alignment for sign logic)
  // Month columns and Total column are center-aligned to clearly show positive (revenue) vs negative (expenses) signs
  const getMonthColumnClasses = (columnId: string, isFirstColumn: boolean) => {
    const isMonthColumn = MONTH_AMOUNT_COLUMN_IDS.includes(columnId);
    const isTotalColumn = columnId === 'totalAmount';
    const shouldCenter = isMonthColumn || isTotalColumn;
    return {
      'text-end': !isFirstColumn && !shouldCenter,
      'text-center': shouldCenter,
    };
  };

  const table = useReactTable({
    data: data.data,
    columns: reportingTableColumnsDefinition,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSubRows: (row) => row.children,
    // To make it available in the column footer definition
    meta: {
      totals: data.totals,
      onMoveVendor,
      onMoveCategory,
      month3,
      month2,
      month1,
      month0,
      isMonth3Highlighted,
      isMonth2Highlighted,
      isMonth1Highlighted,
      isMonth0Highlighted,
      isCurrentMonth,
    },
    state: {
      expanded: expandedState,
    },
    // We need this if we are controlling the expanded state
    onExpandedChange: setExpandedState,
  });

  useEffect(() => {
    // If expandedRows is empty and the component has not been mounted it means that the user is visiting for the first time, so we need to expand the first level by default.
    // If expandedRows has state or the component has been mounted, it means that the user is either back from the vendor category change modal or they are toggling the rows manually.
    const isInitialRender = Object.keys(expandedState).length === 0 && !hasBeenMountedRef.current;

    if (!isInitialRender) {
      return;
    }
    // Creates object with key row.id and value true so the parent rows are initially expanded
    // If we don't provide a getRowId in useReactTable, the default row id is the row index
    // We don't provide the getRowId because if we selected a property like hierarchyLevel from the data structure,
    // the id would not be unique. In that case React complains about key not being unique
    // Maybe there are better ways but for now doing this in the useEffect is the only way I found to expand only the parent rows and make React happy
    // https://github.com/TanStack/table/discussions/4862
    // https://tanstack.com/table/v8/docs/api/core/table#getrowid
    const initialExpandedState = Object.fromEntries(table.getRowModel().rows.map((row) => [row.id, true]));

    setExpandedState(initialExpandedState);
    hasBeenMountedRef.current = true;
  }, [table, setExpandedState, expandedState]);

  useEffect(() => {
    // Scrolls to the selected vendor category when the component is mounted.
    // We keep the dependency array empty so it runs only once when the component is mounted.
    // behavior 'instant' and block 'center' is an explicit product spec
    parentRowRef.current?.scrollIntoView({ behavior: 'instant', block: 'center' });
  }, []);

  useEffect(() => {
    const div = tableRef.current;

    if (!div) return;

    const { width } = div.getBoundingClientRect();

    setTableWidth(width);
  }, []);

  return (
    <div className="flex flex-col gap-5">
      <Table ref={tableRef}>
        <TableHeader className="text-xs text-muted-foreground">
          {/* Standard Headers */}
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id} className="hover:bg-transparent">
              {headerGroup.headers.map((header) => {
                const isFirstColumn = header.column.getIsFirstColumn();
                const isMonthAmountColumn = MONTH_AMOUNT_COLUMN_IDS.includes(header.column.id);
                const isHighlighted = isColumnHighlighted(header.column.id);

                const sumSize = table.getTotalSize();
                const cellSize = header.column.getSize();
                const cellWidthPercentage = cellSize / sumSize;
                const finalWidth = cellWidthPercentage * tableWidth;

                return (
                  <TableHead
                    key={header.id}
                    className={cn('px-2 py-4', {
                      'pl-5 text-start': isFirstColumn,
                      'px-0': header.column.id === 'actions',
                      'pr-0': isMonthAmountColumn,
                      // Leaves enough space for the scrolling bar
                      'pr-6': header.column.getIsLastColumn(),
                      'bg-muted/50': isHighlighted && isMonthAmountColumn,
                      'rounded-t-lg': isHighlighted && isMonthAmountColumn,
                      ...getMonthColumnClasses(header.column.id, isFirstColumn),
                    })}
                    style={{
                      width: `${finalWidth}px`,
                    }}
                  >
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}

          {/* Cash at Start Row */}
          {reportingBalances && (
            <ReportingBalanceRow
              table={table}
              reportingBalances={reportingBalances}
              baseCurrency={baseCurrency}
              tableWidth={tableWidth}
              getMonthColumnClasses={getMonthColumnClasses}
              type="start"
            />
          )}
        </TableHeader>

        <TableBody className="text-sm text-black">
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row, index) => {
              // We don't display rows that have no vendors in them
              if (row.original.vendorCount === 0) {
                return null;
              }

              const isVendorRow = row.original.hierarchyLevel === 'VENDOR';
              const isExpandableRow = row.getCanExpand();
              const shouldScrollToRow = row.id === initialScrollToRowId;

              return (
                <TableRow
                  ref={shouldScrollToRow ? parentRowRef : undefined}
                  key={row.id}
                  // "group/row" is necessary because it is how the children can react to the state of the row i.e group-hover/row
                  className={cn('group/row', {
                    // TODO: use https://tailwindcss.com/docs/hover-focus-and-other-states#first-last-odd-and-even
                    'bg-muted/50': index % 2 !== 0,
                    'cursor-pointer': isVendorRow || isExpandableRow,
                  })}
                  onClick={() => {
                    if (isVendorRow) {
                      entityModalMutations.changeModalView('vendor');

                      entityModalMutations.changeSelectedItem(row.original.id ?? null);

                      analytics.track('reporting_vendor_selected', {
                        vendor_id: row.original.id,
                        vendor_name: row.original.name,
                      });
                    } else if (isExpandableRow) {
                      row.toggleExpanded();
                    }
                  }}
                >
                  {row.getVisibleCells().map((cell) => {
                    const isFirstColumn = cell.column.getIsFirstColumn();
                    const isLastColumn = cell.column.getIsLastColumn();
                    const isVendorRow = row.original.hierarchyLevel === 'VENDOR';
                    const isMonthAmountColumn = MONTH_AMOUNT_COLUMN_IDS.includes(cell.column.id);
                    const isHighlighted = isColumnHighlighted(cell.column.id);

                    const sumSize = table.getTotalSize();
                    const cellSize = cell.column.getSize();
                    const cellWidthPercentage = cellSize / sumSize;
                    const finalWidth = cellWidthPercentage * tableWidth;

                    return (
                      <TableCell
                        key={cell.id}
                        className={cn('px-2', {
                          // Fixed width for the first column
                          // Arbitrary for now based on visual testing - we may need to adjust if horizontal scrolling appears
                          // 'w-[400px]': isFirstColumn,
                          // Rows are vertically more compact in this data table so we override the default padding
                          'py-3': !isVendorRow,
                          // Vendor rows are vertically more compact than category ones
                          'py-2': isVendorRow,
                          // Left padding for top hierarchy level
                          'pl-5':
                            isFirstColumn && ['NO_CATEGORY', 'LEVEL_1_CATEGORY'].includes(row.original.hierarchyLevel),
                          // Left padding for second hierarchy level
                          'pl-12': isFirstColumn && row.original.hierarchyLevel === 'LEVEL_2_CATEGORY',
                          // Left padding for the last hierarchy level (vendor)
                          // This left padding aligns it to the parent row icon above
                          'pl-[75px]': isFirstColumn && isVendorRow,
                          // Actions column is adjusted as possible to save horizontal space
                          'px-0': cell.column.id === 'actions',
                          'pr-0': isMonthAmountColumn,
                          // Leaves enough space for the scrolling bar
                          'pr-6': isLastColumn,
                          'font-semibold':
                            cell.row.depth === 0 && (isFirstColumn || isMonthAmountColumn || isLastColumn),
                          'bg-muted/50': isHighlighted && isMonthAmountColumn,
                          ...getMonthColumnClasses(cell.column.id, isFirstColumn),
                        })}
                        style={{
                          width: `${finalWidth}px`,
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })
          ) : (
            <TableRow>
              <TableCell colSpan={reportingTableColumnsDefinition.length} className="h-24 text-center">
                No results.
              </TableCell>
            </TableRow>
          )}
        </TableBody>

        <TableFooter className="text-sm text-black">
          {table.getFooterGroups().map((footerGroup) => {
            return (
              <TableRow key={footerGroup.id}>
                {footerGroup.headers.map((footer) => {
                  const isFirstColumn = footer.column.getIsFirstColumn();
                  const isLastColumn = footer.column.getIsLastColumn();
                  const isMonthAmountColumn = MONTH_AMOUNT_COLUMN_IDS.includes(footer.column.id);
                  const isHighlighted = isColumnHighlighted(footer.column.id);

                  return (
                    <TableCell
                      key={footer.id}
                      className={cn(
                        // Rows are more compact in this data table so we override the default padding
                        'px-2 py-3',
                        {
                          'pl-5 text-start': isFirstColumn,
                          'pr-0': isMonthAmountColumn,
                          'font-semibold': isFirstColumn || isMonthAmountColumn || isLastColumn,
                          // Leaves enough space for the scrolling bar
                          'pr-6': isLastColumn,
                          'bg-muted/50': isHighlighted && isMonthAmountColumn,
                          ...getMonthColumnClasses(footer.column.id, isFirstColumn),
                        },
                      )}
                    >
                      {flexRender(footer.column.columnDef.footer, footer.getContext())}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}

          {/* Cash at End Row */}
          {reportingBalances && (
            <ReportingBalanceRow
              table={table}
              reportingBalances={reportingBalances}
              baseCurrency={baseCurrency}
              tableWidth={tableWidth}
              getMonthColumnClasses={getMonthColumnClasses}
              type="end"
            />
          )}
        </TableFooter>
      </Table>

      {selectedVendorId && modalView === 'vendor' && (
        <VendorDetailsDrawer
          isOpen={!!selectedVendorId}
          vendor={data.vendors.find((v) => v.id === selectedVendorId) as VendorView}
          categories={categories}
          vendors={data.vendors}
          onClose={() => {
            entityModalMutations.resetSearchParams();
          }}
          onViewAllTransactions={() => {
            const selectedVendor = data.vendors.find((v) => v.id === selectedVendorId);
            const vendorName = selectedVendor?.name;

            if (vendorName) {
              const vendorSearchParam = createSearchParams({
                v: JSON.stringify([vendorName]),
              });

              navigate(`/transactions?${vendorSearchParam}`);
            } else {
              navigate(`/transactions`);
            }

            analytics.track('vendor_transactions_view_all_clicked', {
              vendor_id: selectedVendorId,
              vendor_name: vendorName,
              result_count: selectedVendor?.transactions?.length ?? 0,
              source_page: 'reporting',
            });
          }}
          onSelectTransaction={(transactionId) => {
            navigate(`/transactions?id=${transactionId}`);
          }}
          baseCurrency={baseCurrency}
        />
      )}
    </div>
  );
};
